import { isMobile } from 'react-device-detect'
import { identifyLayiNotifications, trackLayiNotificationsChanged } from './chatbotTracking'

// Browser notification when LAYI finishes an answer while the user is not looking at the chat.
// LAYI's server signals each finished answer with one window message, which Chainlit posts to
// window.parent: this web while the chat is framed (useLayiFrame listens), LAYI's own page for the
// pop-out or a /layi tab (LAYI's custom.js listens). Contract: genai docs/LAYI-FRONTEND-INTEGRATION.md.
//
// The opt-in is one localStorage key on the web's origin, shared with the embedded frame and LAYI's own
// pages. Only the web writes it, together with the notification text in the app's language and the URL
// of the LAYI mark, which LAYI's own pages show too. Missing or unreadable means off. The browser
// permission is a second gate on top of it.
//
// The key belongs to the user who is logged in. Each user's decision in this browser is kept in a record
// next to it, and every page load rewrites the key for that user or removes it (checkLayiNotificationsOnLoad),
// so a second user of the same browser never inherits the first one's notifications. Logout removes the key
// (an open pop-out stops notifying) and keeps the records, like the language, so the next login of a user who
// accepted brings it back. The same check decides whether to show the invite card.
//
// Storage access and the constructor are wrapped: a blocked localStorage never throws, and Chrome on
// Android exposes Notification but refuses to build one.

export const LAYI_NOTIFICATIONS_KEY = 'layi.notifications'

// { "<userId>": { decision, decidedAt, verifiedAt, setupSeenAt, setupHiddenAt, invites, invitedAt } }, times in
// ms. A user without an entry was never asked in this browser. verifiedAt: they saw a test notification.
// setupSeenAt: the setup last opened for them (from the second time it offers "Don't show again"; a verifiedAt
// written before this field existed counts as seen). setupHiddenAt: they chose "Don't show again", so their
// activations here only turn the notifications on, until «Configure» in the user menu removes it.
export const LAYI_NOTIFICATIONS_RECORDS_KEY = 'layi.notifications.users'

// Dispatched on window whenever the key is written or removed, so every switch on screen follows it.
export const LAYI_NOTIFICATIONS_CHANGED_EVENT = 'layi-notifications-change'

export const LAYI_NOTIFICATIONS_DECISIONS = { ACCEPTED: 'accepted', DECLINED: 'declined', BLOCKED: 'blocked' }

// What a click on the switch led to. SETUP (an activation, unless this user hid the setup in this browser) and
// BLOCKED need the modal.
export const LAYI_NOTIFICATIONS_OUTCOMES = { ON: 'on', SETUP: 'setup', BLOCKED: 'blocked', DISMISSED: 'dismissed', FAILED: 'failed' }

// The invite card shows at most three times in a row without an acceptance, per user and browser, a week
// apart, and it counts when it shows (Chrome itself stops asking for a week after three dismissed prompts).
// Accepting starts the count again.
export const LAYI_NOTIFICATIONS_INVITE_MAX = 3
export const LAYI_NOTIFICATIONS_INVITE_EVERY_MS = 7 * 24 * 60 * 60 * 1000

const { ACCEPTED, DECLINED, BLOCKED } = LAYI_NOTIFICATIONS_DECISIONS
const { ON, SETUP, DISMISSED, FAILED } = LAYI_NOTIFICATIONS_OUTCOMES

// The icon every LAYI notification shows: the LAYI mark on a transparent background
// (components/ChatbotWidget/assets/layi-mark.svg rendered 176 px tall and centred in 192 px). It lives in
// public/, so its URL never changes between deploys (a bundled image gets a hashed name, and the URL
// stored for LAYI's own pages would break with the next build). Absolute, because LAYI's pages read it
// from storage.
export const LAYI_NOTIFICATION_ICON_PATH = '/layers-icon-192.png'

export const layiNotificationIcon = () => {
  try {
    return new URL(`${process.env.PUBLIC_URL || ''}${LAYI_NOTIFICATION_ICON_PATH}`, window.location.origin).href
  } catch (error) {
    return undefined
  }
}

// Desktop only: phone browsers have no page notifications.
export const isLayiNotificationsSupported = () => !isMobile && typeof window.Notification === 'function'

const announceChange = () => {
  try {
    window.dispatchEvent(new Event(LAYI_NOTIFICATIONS_CHANGED_EVENT))
  } catch (error) {
    // No events here: each switch reads the state again when it mounts.
  }
}

const isPreferenceOn = () => {
  try {
    const preference = JSON.parse(window.localStorage.getItem(LAYI_NOTIFICATIONS_KEY))
    return Boolean(preference) && preference.on === true
  } catch (error) {
    return false
  }
}

const writePreference = ({ title, body }) => {
  try {
    window.localStorage.setItem(LAYI_NOTIFICATIONS_KEY, JSON.stringify({ on: true, title, body, icon: layiNotificationIcon() }))
  } catch (error) {
    return false
  }
  announceChange()
  return true
}

const removePreference = () => {
  try {
    if (window.localStorage.getItem(LAYI_NOTIFICATIONS_KEY) === null) return
    window.localStorage.removeItem(LAYI_NOTIFICATIONS_KEY)
    announceChange()
  } catch (error) {
    // Storage blocked: nothing was stored either.
  }
}

const readRecords = () => {
  try {
    const records = JSON.parse(window.localStorage.getItem(LAYI_NOTIFICATIONS_RECORDS_KEY))
    return records && typeof records === 'object' && !Array.isArray(records) ? records : {}
  } catch (error) {
    return {}
  }
}

// This user's record in this browser, or null when they were never asked here (or there is no user).
export const readLayiNotificationsRecord = userId => {
  if (!userId) return null
  const record = readRecords()[String(userId)]
  return record && typeof record === 'object' && !Array.isArray(record) ? record : null
}

// Merges a change into this user's record. False without a user, or when the storage refuses it.
const updateRecord = (userId, change) => {
  if (!userId) return false
  try {
    const record = { ...readLayiNotificationsRecord(userId), ...change }
    window.localStorage.setItem(LAYI_NOTIFICATIONS_RECORDS_KEY, JSON.stringify({ ...readRecords(), [String(userId)]: record }))
    return true
  } catch (error) {
    return false
  }
}

// Segment gets every result of the switch (on, off, blocked, dismissed) and, for the decisions, the user's
// latest state on their profile, which can be read across devices (like the CSAT answer).
const report = ({ userId, source, result }) => {
  trackLayiNotificationsChanged({ result, source })
  if (result !== DISMISSED) identifyLayiNotifications({ userId, state: result, at: new Date().toISOString() })
}

// On: opted in on this browser AND allowed by it. The permission is only read once opted in.
export const areLayiNotificationsOn = () => isPreferenceOn() && typeof window.Notification === 'function' && window.Notification.permission === 'granted'

export const showLayiNotification = ({ title, body }) => {
  try {
    const notification = new window.Notification(title, { body, icon: layiNotificationIcon() })
    // Chrome and Safari do nothing on a click by default: bring this window forward.
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  } catch (error) {
    // No page notifications in this browser: nothing to show.
  }
}

// The switch's click, from the user menu row or the invite card. Firefox and Safari only prompt during the
// click, so the permission is requested before anything asynchronous. The answer is stored here, with the
// user's record and Segment, and not in the component, which may be gone by then (the menu closes on a click
// elsewhere). A denied permission is never asked again: only the site settings can lift it. Resolves with
// what the click led to: the setup modal on every activation, unless this user hid it in this browser (then
// the notifications are only turned on). The one test notification is the one the setup's button sends.
export const enableLayiNotifications = ({ userId, source, text }) => {
  const { permission } = window.Notification
  const answer = permission === 'default' ? window.Notification.requestPermission() : permission
  return Promise.resolve(answer)
    .then(result => {
      const now = Date.now()
      if (result === 'granted') {
        if (!writePreference(text)) return FAILED
        const hidden = Boolean((readLayiNotificationsRecord(userId) || {}).setupHiddenAt)
        // Without the record the next load would turn them off again: fail now instead of showing them on.
        if (!updateRecord(userId, { decision: ACCEPTED, decidedAt: now, invites: 0, invitedAt: undefined })) {
          removePreference()
          return FAILED
        }
        report({ userId, source, result: ON })
        return hidden ? ON : SETUP
      }
      if (result === 'denied') {
        updateRecord(userId, { decision: BLOCKED, decidedAt: now })
        report({ userId, source, result: BLOCKED })
        return BLOCKED
      }
      report({ userId, source, result: DISMISSED })
      return DISMISSED
    })
    .catch(() => FAILED)
}

// The switch turned off: this user declined in this browser, so the invite never shows to them here again.
export const disableLayiNotifications = ({ userId, source } = {}) => {
  removePreference()
  updateRecord(userId, { decision: DECLINED, decidedAt: Date.now() })
  report({ userId, source, result: 'off' })
}

// Logout (actions/users.js): LAYI's pages left open stop notifying. The records stay, so the next login of a
// user who accepted here gets the key back from the load check.
export const clearLayiNotificationsKey = () => removePreference()

// The user saw the test notification. Kept in their record; the setup still opens on their next activation.
export const markLayiNotificationsVerified = userId => updateRecord(userId, { verifiedAt: Date.now() })

// Whether this user already saw the setup in this browser: only then does it offer "Don't show again".
export const hasSeenLayiNotificationsSetup = userId => {
  const { setupSeenAt, verifiedAt } = readLayiNotificationsRecord(userId) || {}
  return Boolean(setupSeenAt || verifiedAt)
}

// The setup opened for this user in this browser.
export const markLayiNotificationsSetupSeen = userId => updateRecord(userId, { setupSeenAt: Date.now() })

// "Don't show again": this user's activations in this browser only turn the notifications on.
export const hideLayiNotificationsSetup = userId => updateRecord(userId, { setupHiddenAt: Date.now() })

// «Configure» in the user menu: the setup opens again on this user's activations in this browser.
export const unhideLayiNotificationsSetup = userId => updateRecord(userId, { setupHiddenAt: undefined })

// Keeps the stored text in the app's language while the notifications are on.
export const refreshLayiNotificationsText = text => {
  if (areLayiNotificationsOn()) writePreference(text)
}

// Who gets the invite once the key is off: never someone who turned the notifications off; with the
// permission denied, only who had accepted (once: the record turns blocked when the card shows).
const shouldInvite = ({ decision }, permission) => {
  if (decision === DECLINED) return false
  if (permission !== 'denied') return true
  return decision === ACCEPTED
}

const isInviteDue = ({ invites, invitedAt }, now) => {
  if ((Number(invites) || 0) >= LAYI_NOTIFICATIONS_INVITE_MAX) return false
  return typeof invitedAt !== 'number' || now - invitedAt >= LAYI_NOTIFICATIONS_INVITE_EVERY_MS
}

// Once per page load and user (the invite card runs it on every authenticated page, the chat page included):
// 1. The key follows this user: rewritten, in the current language, when they may use the feature (canInvite:
//    the LAYI role and a user menu to turn it off), accepted in this browser and the permission is granted;
//    removed in every other case. Their record is kept, so the opt-in comes back with the role.
// 2. Whether to invite them. Showing the card counts, so it only shows when the count could be saved.
// Returns true when the card should show.
export const checkLayiNotificationsOnLoad = ({ userId, canInvite, text, now = Date.now() }) => {
  if (!userId || !isLayiNotificationsSupported()) return false
  if (!canInvite) {
    removePreference()
    return false
  }
  const record = readLayiNotificationsRecord(userId) || {}
  const { permission } = window.Notification
  if (record.decision === ACCEPTED && permission === 'granted') {
    writePreference(text)
    return false
  }
  removePreference()
  if (!isInviteDue(record, now) || !shouldInvite(record, permission)) return false
  const lostForGood = record.decision === ACCEPTED && permission === 'denied'
  return updateRecord(userId, { invites: (Number(record.invites) || 0) + 1, invitedAt: now, ...(lostForGood && { decision: BLOCKED, decidedAt: now }) })
}
